import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authFetch } from './common/authFetch';
import { useStudyRoom } from './contexts/StudyRoomContext';
import useTimelapseGenerator from './hooks/useTimelapseRecorder';
import StudyRoomCard from './components/StudyRoomCard';
import StudyRoomHistoryItem from './components/StudyRoomHistoryItem';
import TimelapseDialog from './components/TimelapseDialog';
import HistoryDetailDialog from './components/HistoryDetailDialog';
import ConfirmationDialog from './ConfirmationDialog';
import { getFramesByTimeRange, cleanupExpiredFrames } from './utils/timelapseDB';
import './css/StudyRoom.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudyRoom = () => {
    const { t } = useTranslation();
    const {
        myRoomOpen, openRoom, closeRoom, roomOpenTime,
        tlIsRecording, tlFrameCount,
        stopTimelapseRecording, setTimelapseClosing
    } = useStudyRoom();

    const {
        isGenerating, videoBlob, videoMimeType, generateProgress,
        generateError, generateTimelapse, resetVideo
    } = useTimelapseGenerator();

    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTimelapseDialog, setShowTimelapseDialog] = useState(false);
    const [timelapseDialogMode, setTimelapseDialogMode] = useState('create');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // 히스토리 state
    const [history, setHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);
    const [historyFrameCount, setHistoryFrameCount] = useState(0);
    const historyPageRef = useRef(1);

    // 히스토리 상세 다이얼로그
    const [detailSession, setDetailSession] = useState(null);
    // generate-from-history 모드에서 사용할 세션
    const [genHistorySession, setGenHistorySession] = useState(null);

    // 확인 다이얼로그
    const [confirmDialog, setConfirmDialog] = useState(null);

    // 방 닫기 시 보관할 정보
    const roomTimesRef = useRef(null);
    const closedSessionIdRef = useRef(null);
    const thumbnailRef = useRef(null);

    // sessionStorage로 입장 상태 유지
    const [enteredRooms, setEnteredRooms] = useState(() => {
        try {
            const saved = sessionStorage.getItem('studyroom_entered');
            return saved ? new Set(JSON.parse(saved)) : new Set();
        } catch { return new Set(); }
    });

    useEffect(() => {
        sessionStorage.setItem('studyroom_entered', JSON.stringify([...enteredRooms]));
    }, [enteredRooms]);

    const fetchHistory = useCallback(async (page) => {
        const p = page ?? historyPageRef.current;
        try {
            const response = await authFetch(`${API_URL}/api/v1/studyrooms/history?page=${p}&limit=12`);
            const data = await response.json();
            if (data.success) {
                setHistory(data.sessions || []);
                const currentPage = data.pagination?.page || 1;
                const totalPages = data.pagination?.totalPages || 1;
                // 현재 페이지가 총 페이지를 초과하면 마지막 페이지로
                if (currentPage > totalPages && totalPages > 0) {
                    return fetchHistory(totalPages);
                }
                setHistoryPage(currentPage);
                setHistoryTotalPages(totalPages);
                historyPageRef.current = currentPage;
            }
        } catch { /* silent */ }
    }, []);

    const fetchRooms = useCallback(async () => {
        try {
            const response = await authFetch(`${API_URL}/api/v1/studyrooms`);
            const data = await response.json();
            if (data.success) {
                setRooms(data.rooms);
                const roomIds = new Set(data.rooms.map(r => r.userId));
                setEnteredRooms(prev => {
                    const cleaned = new Set([...prev].filter(id => roomIds.has(id)));
                    return cleaned.size !== prev.size ? cleaned : prev;
                });
            }
        } catch (error) {
            console.error('Failed to fetch study rooms:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHistory();
        cleanupExpiredFrames().catch(() => {});
    }, [fetchHistory]);

    useEffect(() => {
        fetchRooms();
        const ms = (enteredRooms.size > 0 || myRoomOpen) ? 5000 : 30000;
        const interval = setInterval(fetchRooms, ms);
        return () => clearInterval(interval);
    }, [fetchRooms, enteredRooms.size, myRoomOpen]);

    const handleOpenRoom = async () => {
        const success = await openRoom();
        if (success) fetchRooms();
    };

    // ─── 타임랩스 업로드 (XMLHttpRequest로 프로그레스 지원) ───
    const uploadTimelapseToServer = useCallback((sessionId, blob, thumbnailBlob) => {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
            formData.append('video', blob, `timelapse.${ext}`);
            if (thumbnailBlob) {
                formData.append('thumbnail', thumbnailBlob, 'thumbnail.jpg');
            }

            const xhr = new XMLHttpRequest();
            xhr.open('POST', `${API_URL}/api/v1/studyrooms/history/${sessionId}/timelapse/upload`);
            xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('accessToken')}`);

            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    setUploadProgress(Math.round((e.loaded / e.total) * 100));
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    reject(new Error(`Upload failed: ${xhr.status}`));
                }
            };

            xhr.onerror = () => reject(new Error('Network error'));
            xhr.ontimeout = () => reject(new Error('Upload timeout'));
            xhr.timeout = 120000;

            xhr.send(formData);
        });
    }, []);

    const uploadInBackground = useCallback((sessionId, blob, thumbnail) => {
        setUploading(true);
        setUploadProgress(0);
        uploadTimelapseToServer(sessionId, blob, thumbnail)
            .then(() => {
                toast.success(t('studyroom.uploadComplete'));
                fetchHistory();
            })
            .catch((err) => {
                console.error('Timelapse upload failed:', err);
                toast.error(t('studyroom.uploadFailed'));
            })
            .finally(() => {
                setUploading(false);
                setUploadProgress(0);
            });
    }, [uploadTimelapseToServer, fetchHistory, t]);

    // ─── 방 닫기 → 타임랩스 다이얼로그 ───
    const handleCloseRoom = async () => {
        const openTime = roomOpenTime;
        const closeTime = Date.now();

        setTimelapseClosing(true);
        if (tlIsRecording) stopTimelapseRecording();

        const sessionId = await closeRoom();
        closedSessionIdRef.current = sessionId;
        fetchRooms();

        let frameCount = 0;
        if (openTime && sessionId) {
            try {
                const frames = await getFramesByTimeRange(openTime, closeTime);
                frameCount = frames.length;
            } catch { /* silent */ }
        }

        if (frameCount > 0 && sessionId) {
            roomTimesRef.current = { openTime, closeTime };
            setTimelapseDialogMode('create');
            setShowTimelapseDialog(true);
            return;
        }

        setTimelapseClosing(false);
        closedSessionIdRef.current = null;
        fetchHistory();
    };

    const handleTimelapseGenerate = async (fps) => {
        try {
            const { openTime, closeTime } = roomTimesRef.current || {};
            if (!openTime || !closeTime) return;
            const frames = await getFramesByTimeRange(openTime, closeTime);
            if (frames.length === 0) return;
            thumbnailRef.current = frames[0].blob;
            const totalDuration = Math.floor((closeTime - openTime) / 1000);

            // 서버에서 해당 세션의 학습 기록 가져오기
            let studySessions = [];
            const sessionId = closedSessionIdRef.current;
            if (sessionId) {
                try {
                    const res = await authFetch(`${API_URL}/api/v1/studyrooms/history?page=1&limit=1`);
                    const data = await res.json();
                    if (data.success && data.sessions) {
                        const match = data.sessions.find(s => s.session_id === sessionId);
                        if (match) studySessions = match.studySessions || [];
                    }
                } catch { /* silent */ }
            }

            await generateTimelapse(fps, { roomOpenTime: openTime, roomCloseTime: closeTime, totalDuration, studySessions }, frames);
        } catch (err) {
            console.error('handleTimelapseGenerate error:', err);
        }
    };

    const handleTimelapseRegenerate = async (fps) => {
        resetVideo();
        await handleTimelapseGenerate(fps);
    };

    // 타임랩스 저장 (명시적)
    const handleTimelapseSave = () => {
        const blob = videoBlob;
        const sessionId = closedSessionIdRef.current;
        const thumbnail = thumbnailRef.current;
        setShowTimelapseDialog(false);

        if (sessionId && blob) {
            uploadInBackground(sessionId, blob, thumbnail);
        }

        setTimelapseClosing(false);
        closedSessionIdRef.current = null;
        roomTimesRef.current = null;
        thumbnailRef.current = null;
        resetVideo();
        fetchHistory();
    };

    // 타임랩스 버리기 (저장 안함)
    const handleTimelapseDiscard = () => {
        setShowTimelapseDialog(false);
        setTimelapseClosing(false);
        closedSessionIdRef.current = null;
        roomTimesRef.current = null;
        thumbnailRef.current = null;
        resetVideo();
        fetchHistory();
    };

    // 생성 전 건너뛰기 (기존 skip)
    const handleTimelapseSkip = () => {
        setShowTimelapseDialog(false);
        setTimelapseClosing(false);
        closedSessionIdRef.current = null;
        roomTimesRef.current = null;
        fetchHistory();
    };

    // ─── 히스토리 상세 다이얼로그 ───
    const handleDetailOpen = (session) => {
        setDetailSession(session);
    };

    const handleDetailClose = () => {
        setDetailSession(null);
    };

    // 히스토리에서 타임랩스 생성/재생성
    const handleGenerateFromHistory = async (session) => {
        setDetailSession(null); // 상세 닫기
        resetVideo();
        try {
            const openTime = new Date(session.opened_at).getTime();
            const closeTime = new Date(session.closed_at).getTime();
            const frames = await getFramesByTimeRange(openTime, closeTime);
            if (!frames || frames.length === 0) return;

            setGenHistorySession(session);
            setHistoryFrameCount(frames.length);
            setTimelapseDialogMode('generate-from-history');
            setShowTimelapseDialog(true);
        } catch { /* silent */ }
    };

    const handleHistoryGenerate = async (fps) => {
        if (!genHistorySession) return;
        try {
            const openTime = new Date(genHistorySession.opened_at).getTime();
            const closeTime = new Date(genHistorySession.closed_at).getTime();
            const frames = await getFramesByTimeRange(openTime, closeTime);
            if (!frames || frames.length === 0) return;

            const blob = await generateTimelapse(fps, {
                roomOpenTime: openTime,
                roomCloseTime: closeTime,
                totalDuration: genHistorySession.duration_seconds,
                studySessions: genHistorySession.studySessions || []
            }, frames);
            if (blob) {
                const thumbnail = frames[0].blob;
                uploadInBackground(genHistorySession.session_id, blob, thumbnail);
            }
        } catch (err) {
            console.error('handleHistoryGenerate error:', err);
        }
    };

    const handleHistoryDialogClose = () => {
        setShowTimelapseDialog(false);
        setGenHistorySession(null);
        setHistoryFrameCount(0);
        resetVideo();
    };

    // ─── 삭제 핸들러 ───
    const handleDeleteSession = (sessionId) => {
        setConfirmDialog({
            message: t('studyroom.history.deleteSessionConfirm'),
            onConfirm: async () => {
                setConfirmDialog(null);
                try {
                    const response = await authFetch(`${API_URL}/api/v1/studyrooms/history/${sessionId}`, { method: 'DELETE' });
                    const data = await response.json();
                    if (data.success) {
                        setDetailSession(null);
                        fetchHistory();
                    }
                } catch { /* silent */ }
            },
            onCancel: () => setConfirmDialog(null)
        });
    };

    const handleDeleteTimelapse = (sessionId) => {
        setConfirmDialog({
            message: t('studyroom.history.deleteTimelapseConfirm'),
            onConfirm: async () => {
                setConfirmDialog(null);
                try {
                    const response = await authFetch(`${API_URL}/api/v1/studyrooms/history/${sessionId}/timelapse`, { method: 'DELETE' });
                    const data = await response.json();
                    if (data.success) {
                        // 상세 다이얼로그의 세션 정보 업데이트
                        setDetailSession(prev => prev ? { ...prev, has_timelapse: false, video_path: null, thumbnail_path: null } : null);
                        fetchHistory();
                    }
                } catch { /* silent */ }
            },
            onCancel: () => setConfirmDialog(null)
        });
    };

    // ─── 히스토리 페이지네이션 ───
    const handleHistoryPageChange = (newPage) => {
        if (newPage < 1 || newPage > historyTotalPages) return;
        fetchHistory(newPage);
    };

    // ─── 입장/퇴장 ───
    const handleEnter = (userId) => {
        setEnteredRooms(prev => new Set(prev).add(userId));
    };

    const handleLeave = (userId) => {
        setEnteredRooms(prev => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
        });
    };

    // ─── 다이얼로그 Props ───
    const getDialogProps = () => {
        if (timelapseDialogMode === 'generate-from-history') {
            return {
                open: showTimelapseDialog,
                mode: 'generate-from-history',
                frameCount: historyFrameCount,
                isGenerating,
                generateProgress,
                generateError,
                videoBlob,
                videoMimeType,
                onGenerate: handleHistoryGenerate,
                onClose: handleHistoryDialogClose
            };
        }
        return {
            open: showTimelapseDialog,
            mode: 'create',
            frameCount: tlFrameCount,
            isGenerating,
            generateProgress,
            generateError,
            videoBlob,
            videoMimeType,
            onGenerate: handleTimelapseGenerate,
            onRegenerate: handleTimelapseRegenerate,
            onSave: handleTimelapseSave,
            onSkip: handleTimelapseSkip,
            onClose: handleTimelapseDiscard
        };
    };

    return (
        <div className="studyroom-container">
            <div className="studyroom-header">
                <h2>{t('studyroom.title')}</h2>
                <div className="studyroom-header-actions">
                    {uploading && (
                        <div className="studyroom-upload-progress">
                            <div className="studyroom-upload-bar">
                                <div className="studyroom-upload-fill" style={{ width: `${uploadProgress}%` }} />
                            </div>
                            <span className="studyroom-upload-text">{uploadProgress}%</span>
                        </div>
                    )}
                    {myRoomOpen ? (
                        <button className="studyroom-close-btn" onClick={handleCloseRoom}>
                            {t('studyroom.close')}
                        </button>
                    ) : (
                        <button className="studyroom-open-btn" onClick={handleOpenRoom}>
                            {t('studyroom.open')}
                        </button>
                    )}
                    <button className="studyroom-refresh-btn" onClick={fetchRooms}>
                        &#x21bb;
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="studyroom-loading">{t('loading') || 'Loading...'}</div>
            ) : rooms.length === 0 ? (
                <div className="studyroom-empty">{t('studyroom.empty')}</div>
            ) : (
                <div className="studyroom-grid">
                    {rooms.map(room => (
                        <StudyRoomCard
                            key={room.userId}
                            room={room}
                            isEntered={enteredRooms.has(room.userId)}
                            onEnter={() => handleEnter(room.userId)}
                            onLeave={() => handleLeave(room.userId)}
                        />
                    ))}
                </div>
            )}

            {(history.length > 0 || historyPage > 1) && (
                <div className="studyroom-history-section">
                    <div className="studyroom-history-divider" />
                    <h3 className="studyroom-history-title">{t('studyroom.history.title')}</h3>
                    <div className="studyroom-history-list">
                        {history.map(session => (
                            <StudyRoomHistoryItem
                                key={session.session_id}
                                session={session}
                                onClick={handleDetailOpen}
                            />
                        ))}
                    </div>
                    {historyTotalPages > 1 && (
                        <div className="studyroom-pagination">
                            <button
                                className="studyroom-page-btn"
                                disabled={historyPage <= 1}
                                onClick={() => handleHistoryPageChange(historyPage - 1)}
                            >
                                &laquo;
                            </button>
                            {Array.from({ length: historyTotalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === historyTotalPages || Math.abs(p - historyPage) <= 2)
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((item, idx) =>
                                    item === '...' ? (
                                        <span key={`dot-${idx}`} className="studyroom-page-dots">...</span>
                                    ) : (
                                        <button
                                            key={item}
                                            className={`studyroom-page-btn ${item === historyPage ? 'active' : ''}`}
                                            onClick={() => handleHistoryPageChange(item)}
                                        >
                                            {item}
                                        </button>
                                    )
                                )}
                            <button
                                className="studyroom-page-btn"
                                disabled={historyPage >= historyTotalPages}
                                onClick={() => handleHistoryPageChange(historyPage + 1)}
                            >
                                &raquo;
                            </button>
                        </div>
                    )}
                </div>
            )}

            <TimelapseDialog {...getDialogProps()} />

            <HistoryDetailDialog
                session={detailSession}
                onClose={handleDetailClose}
                onDeleteSession={handleDeleteSession}
                onDeleteTimelapse={handleDeleteTimelapse}
                onGenerateTimelapse={handleGenerateFromHistory}
            />

            <ConfirmationDialog
                isOpen={!!confirmDialog}
                message={confirmDialog?.message}
                onConfirm={confirmDialog?.onConfirm}
                onCancel={confirmDialog?.onCancel}
                isDanger
            />
        </div>
    );
};

export default StudyRoom;
